/**
 * Lightweight searchable dropdown.
 *
 * No third-party dep — a controlled combobox pattern purpose-built for
 * the trainer taxonomy picker. Supports:
 *   • Type-to-filter (case-insensitive substring on the option label)
 *   • Arrow-key nav, Enter to select, Escape to close
 *   • Click-outside to close
 *   • disabled + placeholder + a "no matches" empty state
 *
 * Kept intentionally simple: the taxonomy tree tops out around a few
 * dozen items per level, so we don't need windowing or async search.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

export interface Option {
  value: string;
  label: string;
  /** Optional second-line hint (e.g. a course title under a code). */
  hint?: string;
}

interface Props {
  options: Option[];
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Small footprint variant — used inside the taxonomy picker rows. */
  size?: 'sm' | 'md';
}

export const SearchableSelect: React.FC<Props> = ({
  options, value, onChange, placeholder = 'Choose…', disabled, size = 'md',
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);

  const selected = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) =>
      o.label.toLowerCase().includes(needle)
      || (o.hint || '').toLowerCase().includes(needle),
    );
  }, [options, q]);

  useEffect(() => { setCursor(0); }, [q, open]);

  // Click-outside handler — closes the popup.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Focus the search input when the popup opens.
  useEffect(() => {
    if (open) {
      // Slight delay so the browser lays out the input before we focus.
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);

  const commit = (val: string | undefined) => {
    onChange(val);
    setOpen(false);
    setQ('');
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(filtered.length - 1, c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[cursor];
      if (opt) commit(opt.value);
    }
  };

  const isSm = size === 'sm';

  return (
    <div
      ref={rootRef}
      className="relative"
      onKeyDown={onKey}
    >
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={`
          flex w-full items-center justify-between gap-2 rounded-lg border px-3
          text-left outline-none transition
          ${isSm ? 'py-1.5 text-[12.5px]' : 'py-2 text-[13px]'}
          ${disabled
            ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
            : open
              ? 'border-[#1a5276] bg-white text-slate-900 ring-2 ring-[#1a5276]/15'
              : 'border-slate-200 bg-white text-slate-900 hover:border-slate-300'}
        `}
      >
        <span className="min-w-0 flex-1 truncate">
          {selected ? (
            <>
              <span className="font-medium">{selected.label}</span>
              {selected.hint && (
                <span className="ml-1 text-[11px] text-slate-500">· {selected.hint}</span>
              )}
            </>
          ) : (
            <span className="text-slate-400">{placeholder}</span>
          )}
        </span>
        <div className="flex items-center gap-1">
          {selected && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); commit(undefined); }}
              className="flex h-5 w-5 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Clear selection"
            >
              <X size={12} />
            </span>
          )}
          <ChevronDown size={14} className={`text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && !disabled && (
        <div
          className="absolute left-0 right-0 top-full z-[10001] mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_16px_40px_rgba(15,42,64,0.15)]"
          role="listbox"
        >
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
            <Search size={13} className="text-slate-400" />
            <input
              ref={searchRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type to filter…"
              className="min-w-0 flex-1 border-none bg-transparent text-[12.5px] text-slate-900 outline-none placeholder:text-slate-400"
            />
            {q && (
              <button
                type="button"
                onClick={() => { setQ(''); searchRef.current?.focus(); }}
                className="flex h-5 w-5 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={12} />
              </button>
            )}
          </div>

          <div className="max-h-[240px] overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-[12px] text-slate-500">
                No matches for "{q}"
              </div>
            ) : (
              filtered.map((o, i) => {
                const isActive = o.value === value;
                const isCursor = i === cursor;
                return (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => commit(o.value)}
                    className={`
                      flex w-full items-center gap-2 px-3 py-2 text-left transition
                      ${isCursor ? 'bg-[#1a5276]/6' : ''}
                      ${isActive ? 'text-[#1a5276]' : 'text-slate-800'}
                    `}
                  >
                    <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
                      {isActive && <Check size={13} className="text-[#0e9172]" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-[12.5px] ${isActive ? 'font-semibold' : 'font-medium'}`}>
                        {o.label}
                      </span>
                      {o.hint && (
                        <span className="block truncate text-[11px] text-slate-500">
                          {o.hint}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
